import AbstractTrackingService from '@ve/services/analytics/yandexAnalytics/abstractTrackingService';
import type { ReviewSummary, ReviewSummaryTracking } from '../../model/types';
import {
  REVIEW_SUMMARY_ACTION,
  buildReviewSummaryAnalyticsContext,
  getReviewSummaryGoal,
  getTopicsCount,
  getTopicsShownBeforeExpand,
} from './analyticsHelpers';

const trackingService = new AbstractTrackingService();

export const createReviewSummaryTracking = ({
  hotelId,
  summary,
}: {
  hotelId: number | null | undefined;
  summary: ReviewSummary | null;
}): ReviewSummaryTracking => {
  const context = buildReviewSummaryAnalyticsContext(hotelId);
  const praised = summary?.praised ?? [];
  const caveats = summary?.caveats ?? [];

  let impressionObserver: IntersectionObserver | null = null;

  const pageView = (): void => {
    trackingService.setTrackingGoal(getReviewSummaryGoal(REVIEW_SUMMARY_ACTION.PAGE_VIEW), 'other', {
      ...context,
      summary_topics_count: getTopicsCount(praised, caveats),
    });
  };

  const teaserExpandClick = (): void => {
    trackingService.setTrackingGoal(getReviewSummaryGoal(REVIEW_SUMMARY_ACTION.TEASER_EXPAND_CLICK), 'click', context);
  };

  const criteriaExpandClick = (): void => {
    trackingService.setTrackingGoal(getReviewSummaryGoal(REVIEW_SUMMARY_ACTION.FULL_CRITERIA_EXPAND_CLICK), 'click', {
      ...context,
      topics_shown_before_click: getTopicsShownBeforeExpand(praised, caveats),
    });
  };

  const observeImpression = (target: Element | null | undefined): void => {
    if (!target || impressionObserver) return;

    impressionObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      trackingService.setTrackingGoal(getReviewSummaryGoal(REVIEW_SUMMARY_ACTION.FULL_IMPRESSION), 'other', {
        ...context,
        topics_total: getTopicsCount(praised, caveats),
      });
      impressionObserver?.disconnect();
    });
    impressionObserver.observe(target);
  };

  const disconnect = (): void => {
    impressionObserver?.disconnect();
    impressionObserver = null;
  };

  return { pageView, teaserExpandClick, criteriaExpandClick, observeImpression, disconnect };
};
